import React, { useCallback, useState } from 'react';
import {
  Box,
  Chip,
  InputAdornment,
  SvgIcon,
  SxProps,
  TextField,
  Typography,
} from '@mui/material';
import { useFormStoreEvents } from '@brightmls/nextgen.lae.flc';
import ChooseFileButton from './ChooseFileButton';
import { ReactComponent as PDFIcon } from '../icons/pdf.svg';
import UploadDialog from '../../../../../components/edit/media/UploadDialog';
import { useFileUpload } from '../hooks';
import { MediaResource } from '@brightmls/nextgen.lae.flc';

interface UploadFileProps {
  description: string;
  label?: string;
  sx?: SxProps;
}

const UploadFile: React.FC<UploadFileProps> = ({
  description,
  label,
  sx = {},
}) => {
  const [fileToDelete, setFileToDelete] = useState<MediaResource | null>(null);
  const { updateFromListingStateAfter } = useFormStoreEvents();

  const {
    isUploading,
    uploadProgress,
    uploadedFiles,
    onDeleteDocument,
    setPreUploadedFile,
    getRootProps,
    getInputProps,
    open,
    confirmModal,
  } = useFileUpload({
    onSuccessUpload: () => {
      updateFromListingStateAfter(100);
    },
    onConfirm: (file: MediaResource) => {
      if (file) {
        setFileToDelete(file);
        open();
      }
    },
    onCancel: () => {
      setFileToDelete(null);
    },
    handleDrop: async (acceptedFiles: File[]) => {
      try {
        if (fileToDelete) {
          await onDeleteDocument(fileToDelete.MED_VW_KEY);
          setFileToDelete(null);
        }

        setPreUploadedFile(
          acceptedFiles.map((file, index) => ({
            isOfficeExclusive: true,
            id: index,
            file: file,
          })),
        );
      } catch (error) {
        console.error('Upload preparation failed:', error);
      }
    },
  });

  return (
    <Box
      sx={{
        backgroundColor: 'transparent',
      }}
    >
      <Box
        sx={{
          mb: 2,
        }}
      >
        {label}
        {description && (
          <Typography variant="body2" color="#696969">
            {description}
          </Typography>
        )}
      </Box>
      <Box {...getRootProps({ className: 'dropzone' })}>
        <input {...getInputProps()} />
        <TextField
          sx={{
            '.MuiOutlinedInput-root': {
              '&.Mui-readOnly, &.Mui-readOnly.Mui-focused': {
                background: 'white',
              },
            },
            width: 1,
          }}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <ChooseFileButton
                    size="small"
                    variant="text"
                    onClick={(e) => {
                      if (uploadedFiles.length) {
                        confirmModal(uploadedFiles[0]);
                      } else {
                        open();
                      }

                      e.stopPropagation();
                      return null;
                    }}
                  />
                </InputAdornment>
              ),
              startAdornment: uploadedFiles.map((file: MediaResource) => (
                <Chip
                  key={file.MEDIA_SOURCE_FILE_NAME}
                  tabIndex={-1}
                  label={file.MEDIA_SOURCE_FILE_NAME}
                  icon={
                    <SvgIcon
                      component={PDFIcon}
                      fontSize="small"
                      inheritViewBox
                    />
                  }
                  onDelete={(e) => {
                    confirmModal(file);
                    return null;
                  }}
                  sx={{
                    px: 1,
                    '.MuiChip-label': {
                      px: 2,
                    },
                  }}
                />
              )),
              readOnly: true,
            },
          }}
        />
      </Box>
      <UploadDialog
        open={isUploading}
        hidePreview={true}
        uploadProgress={uploadProgress}
      />
    </Box>
  );
};

export default UploadFile;
